import { Box } from '@mui/material';
import dayjs from 'dayjs';

import { Dialog, Text } from 'components';
import { formatPrice } from 'core/shared';
import { replaceText, useTranslations } from 'hooks/useTranslations';

import { CustomString } from 'config/translations';
import { MrrContribution, MrrContributionLine } from 'useQuery/types';

function formatMonth(month: string) {
  return dayjs(month).format('MMM YYYY');
}

// A line covering a single month reads as that month, not as a range of it to
// itself.
function formatPeriod(start: string, end: string) {
  const from = formatMonth(start);
  const to = formatMonth(end);

  return from === to ? from : `${from} – ${to}`;
}

const COLUMNS = {
  description: 240,
  period: 160,
  share: 130,
  total: 130,
};

interface MrrLinesDialogProps {
  // null while the dialog closes, so the content keeps rendering during the
  // fade-out instead of collapsing to an empty box.
  contribution: MrrContribution | null;
  month: string | undefined;
  inclVat: boolean;
  open: boolean;
  onClose: () => void;
}

// The invoice lines behind one customer's share of a month. The table itself
// shows the customer as a single row, netted; this is where the subscription
// and the discount it was invoiced next to become visible again.
function MrrLinesDialog({ contribution, month, inclVat, open, onClose }: MrrLinesDialogProps) {
  const translations = useTranslations('statisticsPage');

  const lines = contribution?.lines ?? [];

  function amountOf(line: MrrContributionLine) {
    return inclVat ? line.amount : line.amount_net;
  }

  function totalOf(line: MrrContributionLine) {
    return inclVat ? line.total_amount : line.total_amount_net;
  }

  function HeaderCell({
    label,
    width,
    flex,
    alignRight,
  }: {
    label: CustomString;
    width: number;
    flex?: boolean;
    alignRight?: boolean;
  }) {
    return (
      <Box
        sx={{
          width,
          flex: flex ? 1 : undefined,
          minWidth: flex ? width : undefined,
          flexShrink: 0,
          textAlign: alignRight ? 'right' : undefined,
        }}
      >
        <Text.Bold>{label}</Text.Bold>
      </Box>
    );
  }

  function LineRow({ line }: { line: MrrContributionLine }) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 1,
          paddingY: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: COLUMNS.description }}>
          <Text>{line.description || line.product_name}</Text>
          <Text.Small color="gray">{line.invoice_reference}</Text.Small>
        </Box>
        <Box sx={{ width: COLUMNS.period, flexShrink: 0 }}>
          <Text>{formatPeriod(line.period_start, line.period_end)}</Text>
          <Text.Small color="gray">
            {replaceText({ text: translations.mrrOverMonths, replaceValues: [line.months] })}
          </Text.Small>
        </Box>
        <Box sx={{ width: COLUMNS.share, flexShrink: 0, textAlign: 'right' }}>
          <Text noWrap>{formatPrice({ price: amountOf(line), symbol: true })}</Text>
        </Box>
        <Box sx={{ width: COLUMNS.total, flexShrink: 0, textAlign: 'right' }}>
          <Text color="gray" noWrap>
            {formatPrice({ price: totalOf(line), symbol: true })}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      title={translations.mrrLinesDialogTitle}
      open={open}
      onChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      maxWidth="md"
      fullWidth
      footer={[
        {
          variant: 'outlined',
          label: translations.close,
          onClick: onClose,
        },
      ]}
    >
      <Box>
        {!!contribution && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              paddingBottom: 2,
            }}
          >
            <Box>
              <Text.Bold>{contribution.customer_name}</Text.Bold>
              <Text.Small color="gray">{month ? formatMonth(month) : ''}</Text.Small>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Text.Small color="gray">
              {replaceText({
                text: translations.mrrLinesCount,
                replaceValues: [lines.length],
              })}
            </Text.Small>
          </Box>
        )}

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 660 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                paddingY: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <HeaderCell label={translations.mrrDescription} width={COLUMNS.description} flex />
              <HeaderCell label={translations.period} width={COLUMNS.period} />
              <HeaderCell label={translations.mrrShare} width={COLUMNS.share} alignRight />
              <HeaderCell label={translations.mrrLineTotal} width={COLUMNS.total} alignRight />
            </Box>

            {lines.map((line) => (
              <LineRow key={line.invoice_line_id} line={line} />
            ))}
          </Box>
        </Box>

        <Box sx={{ paddingTop: 1.5 }}>
          <Text.Small color="gray">
            {inclVat ? translations.mrrAmountsInclVat : translations.mrrAmountsExclVat}
          </Text.Small>
        </Box>
      </Box>
    </Dialog>
  );
}

export { formatMonth, formatPeriod, MrrLinesDialog };
